import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { SnackbarProvider } from "notistack";
import React, { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter } from "react-router-dom";

import Layout from "@/components/layout/Layout";
import { MuiAlertSnackbar } from "@/components/common/MuiAlertSnackbar";
import { AuthProvider } from "@/contexts/AuthContext";
import { LoadingProvider } from "@/contexts/LoadingContext";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { UserTypeProvider } from "@/contexts/UserTypeContext";
import { ThemeProvider } from "@/theme/ThemeProvider";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface AppProps {}

/**
 * Root application component
 * Manages the main layout and page state of the application
 * @component
 */
const App: React.FC<AppProps> = () => {
  const { i18n, t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<Error | null>(null);

  useEffect(() => {
    const loadTranslations = async () => {
      try {
        await i18n.loadNamespaces("translation");
        setIsLoading(false);
      } catch (error) {
        setLoadError(error instanceof Error ? error : new Error("Failed to load translations"));
        setIsLoading(false);
      }
    };

    if (!i18n.isInitialized) {
      loadTranslations();
    } else {
      setIsLoading(false);
    }
  }, [i18n]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-lg text-gray-600">{t("common.loading")}</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-lg text-red-600">{t("common.error")}</div>
      </div>
    );
  }

  return (
    <Suspense>
      <ThemeProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <AuthProvider>
            <UserTypeProvider>
              <SidebarProvider>
                <LoadingProvider>
                  <SnackbarProvider
                    maxSnack={3}
                    Components={{
                      default: MuiAlertSnackbar,
                      error: MuiAlertSnackbar,
                      info: MuiAlertSnackbar,
                      success: MuiAlertSnackbar,
                      warning: MuiAlertSnackbar,
                    }}
                  >
                    <BrowserRouter>
                      <Layout />
                    </BrowserRouter>
                  </SnackbarProvider>
                </LoadingProvider>
              </SidebarProvider>
            </UserTypeProvider>
          </AuthProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </Suspense>
  );
};

export default App;
